import React, { useMemo, useRef, useState, useEffect } from 'react';

import { UserCan } from '../../shared/constants';
import { alertDotIcon } from '../../shared/helpers';
import { iDevicePing, Units } from '../../shared/interfaces';
import { useRedux } from '../../states/redux-state';
import { Point } from './oval-polygon';

export { PointDetails } from './PointDetails/PointDetails';

export const shouldShowPoint = ({ showPOI, showDots }) => (point: iDevicePing) => {
    const ico = showPOI ? alertDotIcon(point.alertActivity) : false;
    // @ts-ignore
    const isSpeed = point.alertActivity.hasspeedingcapped || point.alertActivity.hasspeedingposted;
    const isHidden = point.hidden;

    if (!showPOI && isHidden && (ico || isSpeed)) return false;
    return !(!showDots && !ico && !isSpeed);
}

type IPoisProps = {
    lastPointId?: string;
    tripIdx: number;
    points: Array<iDevicePing>;
    color: string;
    units: Units;
    tripId: string;
    isDeviceMarkerHidden?: boolean;
};

export const Pois = (props: IPoisProps) => {
    const { lastPointId, points, color, units, tripIdx, tripId, isDeviceMarkerHidden = false } = props;
    const user = useRedux(s => s.auth.user.acl);
    const showPOI = useRedux(s => s.gmap.showPOI);
    const showDots = useRedux(s => s.gmap.showDots);
    const bounds = useRedux(s => s.gmap.bounds) || {};

    // Debounce bounds updates during rapid zoom/pan operations (max 100ms frequency)
    // This prevents recalculating viewport points on every single zoom event tick
    const [debouncedBounds, setDebouncedBounds] = useState(bounds);
    const boundsUpdateTimeoutRef = useRef<NodeJS.Timeout>();

    useEffect(() => {
        // Clear existing timeout
        if (boundsUpdateTimeoutRef.current) {
            clearTimeout(boundsUpdateTimeoutRef.current);
        }

        // Schedule bounds update with debounce delay
        boundsUpdateTimeoutRef.current = setTimeout(() => {
            setDebouncedBounds(bounds);
        }, 100);

        return () => {
            if (boundsUpdateTimeoutRef.current) {
                clearTimeout(boundsUpdateTimeoutRef.current);
            }
        };
    }, [bounds]);

    const hasBounds = Number.isFinite(debouncedBounds?.latNorth)
        && Number.isFinite(debouncedBounds?.latSouth)
        && Number.isFinite(debouncedBounds?.lngEast)
        && Number.isFinite(debouncedBounds?.lngWest);

    let filteredPois = points;

    if (!showDots || !showPOI) {
        filteredPois = points.filter(shouldShowPoint({ showDots, showPOI }));
    }

    if (showDots && !user.can.includes(UserCan.HIDE_POINTS)) {
        filteredPois = points.filter(point => {
            return !point.hidden;

        })
    }

    const checkedPoints = tripId === 'pointsWithoutTripId' ? points : filteredPois;

    const pointsInViewport = useMemo(() => {
        if (!hasBounds) return checkedPoints;

        const latPad = Math.max((debouncedBounds.latNorth - debouncedBounds.latSouth) * 0.2, 0.01);
        const lngPad = Math.max((debouncedBounds.lngEast - debouncedBounds.lngWest) * 0.2, 0.01);

        const minLat = debouncedBounds.latSouth - latPad;
        const maxLat = debouncedBounds.latNorth + latPad;
        const minLng = debouncedBounds.lngWest - lngPad;
        const maxLng = debouncedBounds.lngEast + lngPad;

        return checkedPoints.filter((point) => {
            const location = point?.coordinates?.location;
            if (!location) return false;
            return location.lat >= minLat
                && location.lat <= maxLat
                && location.lng >= minLng
                && location.lng <= maxLng;
        });
    }, [checkedPoints, hasBounds, debouncedBounds?.latNorth, debouncedBounds?.latSouth, debouncedBounds?.lngEast, debouncedBounds?.lngWest]);

    const renderPoints = useMemo(() => {
        const MAX_VISIBLE_MARKERS = 1200;

        if (pointsInViewport.length <= MAX_VISIBLE_MARKERS) return pointsInViewport;

        const stride = Math.ceil(pointsInViewport.length / MAX_VISIBLE_MARKERS);
        const sampled = pointsInViewport.filter((_, idx) => idx % stride === 0);

        if (lastPointId && !sampled.some((p) => p.pointId === lastPointId)) {
            const lastPoint = pointsInViewport.find((p) => p.pointId === lastPointId);
            if (lastPoint) sampled.push(lastPoint);
        }

        return sampled;
    }, [pointsInViewport, lastPointId]);

    return (
        <>
            {renderPoints.map((point, idx) => (
                <Point
                    key={point.pointId}
                    point={point}
                    tripIdx={tripIdx}
                    color={color}
                    isLastPoint={lastPointId === point.pointId}
                    units={units}
                    idx={idx + 1}
                    icoSizeDiff={22} // sos slightly bigger cause of text
                    isDeviceMarkerHidden={isDeviceMarkerHidden}
                />
            ))}
        </>
    );
}

